import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../components/shared/PageContainer";
import { Alert, Spinner } from "../components/ui";
import { ordersApi } from "../services/api";
import { Order } from "../types";

interface OverviewCard {
  icon: string;
  label: string;
  chip: string;
  /** Tint for the icon chip and badge — one accent per tone. */
  tone: string;
  count: number;
}

export default function Dashboard() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await ordersApi.getOrders();
        setOrders(response || []);
      } catch {
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const overviewCards = useMemo<OverviewCard[]>(() => {
    const pendingOrders = orders.filter((order) => {
      const status = (order.status || "").toLowerCase();
      return status.includes("pending") || status.includes("progress");
    }).length;

    const urgentOrders = orders.filter((order) => {
      if (!order.dateRequired) return false;

      const requiredDate = new Date(order.dateRequired);
      if (Number.isNaN(requiredDate.getTime())) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      requiredDate.setHours(0, 0, 0, 0);

      const diffInDays = Math.ceil(
        (requiredDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );
      const status = (order.status || "").toLowerCase();

      return diffInDays <= 2 && !status.includes("complete");
    }).length;

    return [
      {
        icon: "bi-basket2-fill",
        label: "Total Orders",
        chip: "Lifetime",
        tone: "bg-brand/10 text-brand-strong ring-brand/20",
        count: orders.length,
      },
      {
        icon: "bi-clock-fill",
        label: "Pending Orders",
        chip: "In Queue",
        tone: "bg-amber-500/10 text-amber-700 ring-amber-500/20",
        count: pendingOrders,
      },
      {
        icon: "bi-exclamation-lg",
        label: "Urgent Orders",
        chip: "Critical",
        tone: "bg-red-500/10 text-red-700 ring-red-500/20",
        count: urgentOrders,
      },
    ];
  }, [orders]);

  return (
    <PageContainer>
      <header>
        <p className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
          Daily Overview
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">
          Admin Dashboard
        </h1>
      </header>

      {error && <Alert tone="warning">{error}</Alert>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {overviewCards.map((card) => (
          <section key={card.label} className="panel p-5">
            <div className="flex items-start justify-between gap-3">
              <span
                className={`inline-flex items-center justify-center rounded-xl p-2.5 ring-1 ${card.tone}`}
              >
                <i className={`bi ${card.icon} text-lg`} />
              </span>
              <span
                className={`rounded-lg px-2.5 py-1 text-xs font-medium ring-1 ${card.tone}`}
              >
                {card.chip}
              </span>
            </div>

            <p className="mt-4 text-sm text-zinc-500">{card.label}</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums">
              {loading ? <Spinner size="size-7" /> : card.count.toLocaleString()}
            </p>
          </section>
        ))}
      </div>
    </PageContainer>
  );
}
